import { Table, TableBody, TableCell } from "@material-ui/core";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { THEME } from "../../theme/ThemeStore";
import { getKanbanStatusOptions } from "../../constants";
import { GetApplicantsObject } from "../../network/APITypes";
import { generalStore } from "../../stores/GeneralStore";
import { TableHeaderOption, TableStore } from "../../stores/TableStore";
import { applicantListHeaderEnum, JobCategoryEnum } from "../../types/models";
import { pushRoute } from "../app/router/history";
import { TableSelection } from "../hooks/useTableSelection";
import { HrRoutes } from "../hr/router/HrRoutes";
import { localeToFormat, optionToString } from "../util/helpers";
import { NoResults } from "./EmptyStates";
import { DivisionTag } from "./Primitives";
import { TableHeader } from "./TableHeader";
import { TableLine } from "./TableLine";
import { t } from "../../i18n/util";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

const applicantHeaderFieldIdToContent = (id: applicantListHeaderEnum, applicant: GetApplicantsObject) => {
    switch (id) {
        case "name":
            return (
                <div
                    style={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        width: 0,
                        flexGrow: 1
                    }}
                >
                    {`${applicant.firstName} ${applicant.lastName}`}
                </div>
            );
        case "division":
            return <DivisionTag division={applicant.jobCategory as JobCategoryEnum} />;
        case "salary":
            return (
                <div
                    style={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        width: 0,
                        flexGrow: 1
                    }}
                >
                    {`€ ${new Intl.NumberFormat(localeToFormat(generalStore.locale)).format(applicant.salary)}`}
                </div>
            );
        case "lastChange":
            return moment(applicant.lastChange).format(t("screen.dateFormat"));
        case "status":
            return optionToString(getKanbanStatusOptions(), applicant.applicationState);
        case "createdAt":
            return moment(applicant.applicationDate).format(t("screen.dateFormat"));
        case "actions":
            return (
                <div>
                    <OpenInNewIcon
                        onClick={(event) => {
                            event.stopPropagation();

                            window.open(HrRoutes.APPLICANT.DETAILS.replace(":applicantId", applicant.id), "_blank");
                        }}
                    />
                </div>
            );
        default:
            break;
    }
};

type Props = {
    selectElements?: TableSelection;
    tableStore: TableStore;
    applicants: GetApplicantsObject[];
    initialLoaded?: boolean;
    responsiveHeaderOptions: TableHeaderOption<any>[];
    tableCellWidthProportionsArray: number[];
    hideCheckboxes?: boolean;
};

export const HrBotApplicantsList = observer(
    ({
        selectElements,
        tableStore,
        applicants,
        initialLoaded,
        responsiveHeaderOptions,
        tableCellWidthProportionsArray,
        hideCheckboxes
    }: Props) => {
        const loaded = !generalStore.isLoading && initialLoaded;

        return (
            <div style={{ width: "100%", maxWidth: "100%" }}>
                <Table>
                    <TableHeader
                        fields={responsiveHeaderOptions}
                        select={selectElements}
                        flexGrowArray={tableCellWidthProportionsArray}
                        tableStore={tableStore}
                        hideCheckbox={hideCheckboxes}
                    />
                    {loaded && applicants.length > 0 && (
                        <TableBody>
                            {applicants.map((applicant, applicantIndex) => (
                                <TableLine
                                    id={applicant.id}
                                    select={selectElements}
                                    key={applicantIndex}
                                    hideCheckbox={hideCheckboxes}
                                >
                                    {responsiveHeaderOptions.map((option, headerFieldIndex) => (
                                        <TableCell
                                            key={headerFieldIndex}
                                            className="body2"
                                            style={{
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                color: THEME.customColors.headingsDark,
                                                flex: tableCellWidthProportionsArray[headerFieldIndex],
                                                cursor: "pointer"
                                            }}
                                            onClick={() => {
                                                pushRoute(HrRoutes.APPLICANT.DETAILS, {
                                                    params: { applicantId: applicant.id }
                                                });
                                            }}
                                        >
                                            {applicantHeaderFieldIdToContent(option.value, applicant)}
                                        </TableCell>
                                    ))}
                                </TableLine>
                            ))}
                        </TableBody>
                    )}
                </Table>
                {loaded && applicants.length === 0 && <NoResults />}
            </div>
        );
    }
);
